// ============================================================================
// DIE SCHNITTSTELLE FÜR FREMDE SYSTEME.
//
//   ORIGINALTEXT MARVIN (06.09.2026)
//     „dann ist noch der offene punkt mit der API schnittstelle mit token,
//      einer kurzen anleitung zur einrichtung, damit man die daten dort
//      hochladen und eintragen kann, aber auch auslesen. das muss über ein
//      verschlüsselte kommunikation/protokoll sein."
//
// ZWEI REITER, WEIL ES ZWEI FRAGEN SIND: „welche Schlüssel habe ich vergeben"
// und „welche Wege gibt es überhaupt". Die zweite stellt jeder genau einmal,
// nämlich beim Einrichten - und bekam bisher gar keine Antwort.
// ============================================================================

import { useEffect, useState } from 'react';
import { api, zeitpunkt, datum, darf, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import {
  Dialog, Fehlerkasten, Formular, Text, Auswahl, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import Bruchtext from '../teile/Bruchtext.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

type Schluessel = {
  id: string; name: string; zweck: string | null; kennung: string;
  aktiv: boolean; gilt_bis: string | null;
  zuletzt_am: string | null; zuletzt_ip: string | null; zugriffe: string;
  gesperrt_am: string | null; gesperrt_grund: string | null;
  created_at: string;
  benutzer_id: string; benutzer: string; email: string | null;
  firma: string | null;
  rechte_anzahl: string; auswahl_anzahl: string; auswahl: string[];
};

type Weg = { methode: string; weg: string; rechte: string[]; eines: boolean };

/**
 * EIN KANAL, AUF DEM EINE DATEI HEREINKOMMT (Punkt 4b, 08.09.2026).
 *
 * `mitSchluessel` und `gibtEs` sind AUSGERECHNET und stehen deshalb nicht in
 * der Oberfläche: das erste kommt aus der Sperrliste, das zweite aus dem
 * Verzeichnis der wirklich angemeldeten Wege.
 */
type Kanalfeld = { name: string; art: 'datei' | 'text'; pflicht: boolean;
                   zweck: string };
type Kanal = {
  schluessel: string; name: string; methode: string; weg: string;
  recht: string | null; felder: Kanalfeld[]; strecke: string; bleibt: string;
  mehrteilig: boolean; mitSchluessel: boolean; gibtEs: boolean;
};
type Kanalstand = { kanaele: Kanal[]; fuerSchluessel: string };

export default function Schnittstelle({ ich }: { ich: Anmelder }) {
  const [reiter, setReiter] =
    useState<'schluessel' | 'wege' | 'pruefen' | 'anleitung'>('schluessel');
  const [liste, setListe] = useState<Schluessel[] | null>(null);
  const [wege, setWege] = useState<Weg[] | null>(null);
  /* DIE EIGENE ADRESSE FÜR DIE BEISPIELE. Sie kommt vom Server: nur er
     weiss, unter welchem Namen er von aussen erreichbar ist. */
  const [basis, setBasis] = useState<string | null>(null);
  /* DIE KANÄLE, AUF DENEN EINE DATEI HEREINKOMMT (Punkt 4b). Sie kommen
     ebenfalls vom Server: die Frage „geht das auch mit einem Schlüssel" ist
     eine Rechnung aus der Sperrliste und keine Angabe, die hier stünde. */
  const [kanaele, setKanaele] = useState<Kanalstand | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [neu, setNeu] = useState(false);
  const [gezeigt, setGezeigt] = useState<
    { klartext: string; kennung: string } | null>(null);

  const pflegen = darf(ich, 'schnittstelle.verwalten');

  async function laden() {
    setFehler(null);
    try { setListe((await api.apiSchluessel()).schluessel); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  useEffect(() => {
    if (reiter !== 'wege' || wege) return;
    api.apiWege().then((a) => setWege(a.wege)).catch((f) => setFehler(ausFehler(f)));
  }, [reiter]);
  /* GEHOLT WIRD SIE ERST, WENN DIE ANLEITUNG AUFGEHT - und genau einmal.
     Ein Aufruf beim Öffnen des Bildschirms wäre eine Anfrage, die in neun
     von zehn Fällen niemand braucht. */
  useEffect(() => {
    if (reiter !== 'anleitung' || basis) return;
    api.apiAdresse().then((a) => setBasis(a.basis)).catch(() => setBasis(''));
    /* DIE KANÄLE DANEBEN, UND EIN FEHLSCHLAG HÄLT DIE ANLEITUNG NICHT AUF.
       Die 5 Schritte und die Beispiele stehen ohne sie da; eine Anleitung,
       die ganz ausfällt, weil eine Karte darin nicht kommt, wäre die
       teurere Antwort. */
    api.apiKanaele().then(setKanaele).catch(() => setKanaele(null));
  }, [reiter]);

  return (
    <>
      <Kopf
        titel="Schnittstelle"
        weg="Ein fremdes System holt Daten und trägt Daten ein - mit einem Schlüssel."
        rechts={pflegen && (
          <div className="reihe">
            <button className="knopf haupt" onClick={() => setNeu(true)}>
              Schlüssel anlegen
            </button>
          </div>
        )}
      />

      <div className="inhalt">
        <Fehlerkasten lage={fehler} />

        {/* EINE CHIPREIHE IST EINE REITERLEISTE UND KEINE KNOPFLEISTE.
            `reiterreihe` klebt beim Rollen oben (seit .96); `reihe` allein
            wäre eine beliebige Zeile und würde mitrollen. */}
        <div className="reihe reiterreihe">
          {([['schluessel', `Schlüssel${liste ? ` (${liste.length})` : ''}`],
             ['wege', `Wege${wege ? ` (${wege.length})` : ''}`],
             /* SEIT DEM 18.09.2026. Marvin: „wir haben probleme mit der api.
                die daten laufen nicht sauber über die schnitt stelle. so
                fehlen dem partner zb. die aufträge ... bitte valide prüfen."
                Fehlt einem Partner ein Auftrag, gibt die Antwort „0 von 0"
                keinen Grund her. Dieser Reiter nennt ihn. */
             ['pruefen', 'Prüfen'],
             ['anleitung', 'Anleitung']] as const)
            .map(([r, t]) => (
              <button key={r} className="knopf" onClick={() => setReiter(r)}
                style={reiter === r
                  ? { borderColor: 'var(--akzent)', color: 'var(--akzent)' }
                  : undefined}>
                {t}
              </button>
            ))}
        </div>

        {reiter === 'schluessel'
          ? <Schluesselliste liste={liste} pflegen={pflegen} neuLaden={laden} />
          : reiter === 'wege'
            ? <Wegeliste wege={wege} />
          : reiter === 'pruefen'
            ? <Sichtbarkeitspruefung />
            : <Anleitung basis={basis} kanaele={kanaele} />}
      </div>

      {neu && (
        <Dialog titel="Schlüssel anlegen" breite={720} zu={() => setNeu(false)}>
          <NeuerSchluessel
            fertig={(k) => { setNeu(false); setGezeigt(k); laden(); }}
          />
        </Dialog>
      )}

      {/* GENAU EINMAL. Danach steht in der Datenbank nur die Prüfsumme, und
          es gibt keinen Weg, der ihn ein zweites Mal herausgibt. Deshalb
          schliesst dieses Fenster NICHT mit einem Klick daneben aus Versehen
          zu wenig: der Knopf sagt, was er bedeutet. */}
      {gezeigt && (
        <Dialog titel="Der Schlüssel" breite={720} zu={() => setGezeigt(null)}>
          <div className="hinweis">
            <b>Jetzt kopieren.</b> Er wird nicht noch einmal gezeigt - in der
            Datenbank steht ab sofort nur seine Prüfsumme.
          </div>
          <pre className="schluesselkasten"><code>{gezeigt.klartext}</code></pre>
          <div className="reihe">
            <button className="knopf haupt"
                    onClick={() => navigator.clipboard?.writeText(gezeigt.klartext)}>
              In die Zwischenablage
            </button>
            <button className="knopf" onClick={() => setGezeigt(null)}>
              Ich habe ihn
            </button>
          </div>
          <p className="klein leise" style={{ marginTop: 10 }}>
            In der Liste steht er ab jetzt unter der Kennung{' '}
            <code>{gezeigt.kennung}</code>.
          </p>
        </Dialog>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function Schluesselliste({
  liste, pflegen, neuLaden,
}: { liste: Schluessel[] | null; pflegen: boolean; neuLaden: () => void }) {
  const [sperre, setSperre] = useState<Schluessel | null>(null);
  const [weg, setWeg] = useState<Schluessel | null>(null);
  const [darfWege, setDarfWege] = useState<Schluessel | null>(null);
  /* WESSEN SCHLÜSSEL. Marvin, 06.09.2026: „wo sehe ich denn die schlüssel,
     die ich der person geordnet habe?" Die Spalte „Zugang" steht seit .122
     da; was fehlte, war der WEG dorthin - bei zwanzig Schlüsseln liest man
     eine Spalte nicht mehr durch. Es ist eine ANZEIGE über Zeilen, die schon
     geladen sind, und keine zweite Grenze: WER Schlüssel überhaupt sieht,
     entscheidet das Recht am Weg. */
  const [wessen, setWessen] = useState('');

  if (!liste) return <Karte><Leer text="Wird geladen." /></Karte>;
  if (!liste.length) {
    return (
      <Karte titel="Noch kein Schlüssel">
        <Leer text={'Ohne Schlüssel kommt kein fremdes System herein. '
                  + '„Schlüssel anlegen" oben rechts.'} />
      </Karte>
    );
  }

  /* JE ZUGANG EINER, und die Zahl daneben. Ein Auswahlfeld, das jeden Zugang
     des Hauses anbietet, hätte für die meisten null Treffer - hier stehen nur
     die, an denen wirklich ein Schlüssel hängt. */
  const zugaenge = [...new Map(
    liste.map((k) => [k.benutzer_id, k])).values()]
    .map((k) => ({
      id: k.benutzer_id,
      text: k.benutzer + (k.firma ? ` (${k.firma})` : ''),
      n: liste.filter((x) => x.benutzer_id === k.benutzer_id).length,
    }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const gezeigt = wessen ? liste.filter((k) => k.benutzer_id === wessen) : liste;

  return (
    <>
      <Karte titel="Vergebene Schlüssel">
        {zugaenge.length > 1 && (
          <div className="reihe" style={{ marginBottom: 10 }}>
            <label className="feldpaar">
              <span className="klein leise">Zugang</span>
              <select value={wessen} onChange={(e) => setWessen(e.target.value)}
                      style={{ minWidth: 260 }}>
                <option value="">alle ({liste.length})</option>
                {zugaenge.map((z) => (
                  <option key={z.id} value={z.id}>{z.text} ({z.n})</option>
                ))}
              </select>
            </label>
            {/* EIN FILTER, DER GREIFT, SAGT ES. Sonst sieht eine gefilterte
                Liste aus wie ein Haus mit weniger Schlüsseln. */}
            {wessen && (
              <button className="knopf" onClick={() => setWessen('')}>
                Alle anzeigen ({liste.length})
              </button>
            )}
          </div>
        )}
        <table className="liste">
            <thead>
              <tr>
                <th>Name</th><th>Kennung</th><th>Zugang</th>
                <th>Rechte</th><th>Zuletzt</th><th>Stand</th>
                {pflegen && <th></th>}
              </tr>
            </thead>
            <tbody>
              {gezeigt.map((k) => (
                <tr key={k.id} className={k.aktiv ? undefined : 'leise'}>
                  <td>
                    <b>{k.name}</b>
                    {k.zweck && <div className="klein leise">{k.zweck}</div>}
                  </td>
                  {/* DIE KENNUNG IST DIE ZEILE. Sie steht im Klartext im
                      Schlüssel und sagt nichts über sein Geheimnis - sie ist
                      der einzige Weg, in einer Liste den wiederzuerkennen,
                      den man gerade in der Hand hält. */}
                  <td><code>{k.kennung}</code></td>
                  <td>
                    <Bruchtext text={k.benutzer} />
                    {k.firma && <div className="klein leise">{k.firma}</div>}
                  </td>
                  <td>
                    {Number(k.auswahl_anzahl) > 0
                      ? <span title={k.auswahl.join(', ')}>
                          {k.rechte_anzahl} (eingeschränkt)
                        </span>
                      : <>{k.rechte_anzahl} <span className="klein leise">
                          wie der Zugang</span></>}
                  </td>
                  <td>
                    {k.zuletzt_am
                      ? <>{zeitpunkt(k.zuletzt_am)}
                          <div className="klein leise">
                            {k.zugriffe} Zugriffe
                          </div></>
                      /* NOCH NIE GEZOGEN IST EINE AUSKUNFT UND KEIN MANGEL.
                         Ein frisch angelegter Schlüssel hat noch nichts
                         getan; einer, der seit Wochen nichts tut, ist
                         entweder vergessen oder verloren. */
                      : <span className="klein leise">noch nie</span>}
                  </td>
                  <td>
                    {!k.aktiv
                      ? <span className="marke-zustand z-ueberfaellig" title={k.gesperrt_grund ?? ''}>
                          gesperrt
                        </span>
                      : k.gilt_bis
                        ? <span className="marke-zustand z-laufend">
                            bis {datum(k.gilt_bis)}
                          </span>
                        : <span className="marke-zustand z-erledigt">aktiv</span>}
                  </td>
                  {pflegen && (
                    <td className="knopfleiste breit">
                      {/* WAS ER DARF - die zweite Frage nach „welche Wege gibt
                          es überhaupt". Marvin, 06.09.2026: „wie kann er die
                          verfügbaren APIs denn sehen?" Zwischen „75 Rechte" in
                          der Spalte daneben und „diese 142 Wege" liegt genau
                          die Rechnung, die der Server macht. */}
                      <button className="knopf" onClick={() => setDarfWege(k)}>
                        Was er darf
                      </button>
                      <button className="knopf" onClick={() => setSperre(k)}>
                        {k.aktiv ? 'Sperren' : 'Freigeben'}
                      </button>
                      <button className="knopf" onClick={() => setWeg(k)}>
                        Löschen
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
        </table>
      </Karte>

      {sperre && (
        <SperrFenster k={sperre} zu={() => setSperre(null)}
                      fertig={() => { setSperre(null); neuLaden(); }} />
      )}
      {weg && (
        <LoeschFenster k={weg} zu={() => setWeg(null)}
                       fertig={() => { setWeg(null); neuLaden(); }} />
      )}
      {darfWege && (
        <DarfFenster k={darfWege} zu={() => setDarfWege(null)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function NeuerSchluessel({
  fertig,
}: { fertig: (k: { klartext: string; kennung: string }) => void }) {
  const [zugaenge, setZugaenge] = useState<any[] | null>(null);
  const [name, setName] = useState('');
  const [zweck, setZweck] = useState('');
  const [benutzerId, setBenutzerId] = useState('');
  const [gilt, setGilt] = useState('');
  const [auswahl, setAuswahl] = useState<string[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.apiZugaenge().then((a: any) => setZugaenge(a.zugaenge))
       .catch((f) => setFehler(ausFehler(f)));
  }, []);

  /* WAS DIESER MENSCH ÜBERHAUPT DARF, entscheidet, was hier ankreuzbar ist.
     Ein Recht, das er nicht hat, weist der Server ab - und das ist richtig:
     wer es ankreuzt, glaubt sonst, er habe es vergeben. Hier steht es gar
     nicht erst zur Wahl. */
  const gewaehlt = zugaenge?.find((b: any) => b.id === benutzerId);
  const seine: string[] = gewaehlt?.rechte ?? [];

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      const a = await api.apiSchluesselAnlegen({
        benutzer_id: benutzerId, name, zweck: zweck || null,
        gilt_bis: gilt || null, rechte: auswahl,
      });
      fertig({ klartext: a.klartext, kennung: a.kennung });
    } catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
  }

  return (
    <Formular
      geaendert={!!name && !!benutzerId} laeuft={laeuft} fehler={fehler}
      speichern={speichern} knopfText="Anlegen"
    >
      <Text name="name" titel="Name" pflicht wert={name} setze={setName}
            fehler={fehler} breite="100%"
            hinweis="Er sagt später, WER diesen Schlüssel benutzt."
            platzhalter="n8n Mailversand" />
      <Text name="zweck" titel="Wofür" wert={zweck} setze={setZweck}
            fehler={fehler} breite="100%" mehrzeilig />
      {/* WER NICHT HEREINKOMMT, STEHT NICHT ZUR WAHL - UND ENTSCHIEDEN HAT
          DAS SCHON DIE ABFRAGE. Hier stand dieselbe Bedingung ein zweites
          Mal (`b.aktiv && !b.wartet`), und der Weg liefert diese beiden
          Felder gar nicht: `undefined && …` ist falsch, also fiel JEDE Zeile
          heraus, und das Feld war für jeden und immer leer. Zwei Abschriften
          einer Regel laufen auseinander - diese beiden waren es vom ersten
          Tag an, und gemessen wurde der WEG und nicht der BILDSCHIRM. Was der
          Bildschirm liest, misst 102 Abschnitt 4 gegen eine echte Antwort;
          dass hier wirklich etwas zur Wahl steht, misst 015 im Browser. */}
      <Auswahl
        name="benutzer_id" titel="Hängt an diesem Zugang" pflicht
        wert={benutzerId} setze={(w) => { setBenutzerId(w); setAuswahl([]); }}
        fehler={fehler} breite="100%"
        hinweis={zugaenge === null
          ? 'Die Zugänge werden geholt.'
          : zugaenge.length === 0
            ? 'Es steht kein Zugang zur Wahl. Ein Schlüssel hängt immer an '
              + 'einem Zugang, der auch selbst hereinkommt - unter „Benutzer '
              + 'und Rechte" einen anlegen oder freischalten.'
            : 'Der Schlüssel kann genau das, was dieser Mensch darf - nie mehr.'}
        optionen={(zugaenge ?? []).map((b: any) => ({
          wert: b.id,
          text: b.name + (b.firma ? ` (${b.firma})` : '')
              + (b.email ? `, ${b.email}` : ''),
        }))}
      />
      <Text name="gilt_bis" titel="Gilt bis" art="date" wert={gilt}
            setze={setGilt} fehler={fehler}
            hinweis={'Freiwillig. Ein Schlüssel für einen laufenden Zusteller '
                   + 'sollte KEINE Frist haben - sonst steht der Betrieb an '
                   + 'einem Tag, an den niemand gedacht hat.'} />

      {benutzerId && (
        <div style={{ marginTop: 12 }}>
          <b>Nur diese Rechte</b>
          <p className="klein leise" style={{ margin: '4px 0 8px' }}>
            Nichts angekreuzt heißt: so viel wie der Zugang selbst
            ({seine.length} Rechte). Angekreuzt wird nur abgeschnitten.
          </p>
          <div className="nennliste">
            {seine.map((r) => (
              <label key={r} className="wahlzeile">
                <input type="checkbox" checked={auswahl.includes(r)}
                       onChange={(e) => setAuswahl(
                         e.target.checked
                           ? [...auswahl, r]
                           : auswahl.filter((x) => x !== r))} />
                <span><code>{r}</code></span>
              </label>
            ))}
            {!seine.length && (
              <Leer text="Dieser Zugang trägt kein einziges Recht." />
            )}
          </div>
        </div>
      )}
    </Formular>
  );
}

// ---------------------------------------------------------------------------

function SperrFenster({
  k, zu, fertig,
}: { k: Schluessel; zu: () => void; fertig: () => void }) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const sperren = k.aktiv;

  async function los() {
    setLaeuft(true); setFehler(null);
    try { await api.apiSchluesselSperre(k.id, sperren, grund); fertig(); }
    catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
  }

  return (
    <Dialog titel={sperren ? 'Schlüssel sperren' : 'Schlüssel freigeben'}
            untertitel={`${k.name} (${k.kennung})`} breite={620} zu={zu}>
      <Formular geaendert={!sperren || !!grund} laeuft={laeuft} fehler={fehler}
                speichern={los}
                knopfText={sperren ? 'Sperren' : 'Freigeben'}>
        {sperren ? (
          <>
            <div className="hinweis">
              Gesperrt heißt: er antwortet ab sofort mit einer Abweisung.{' '}
              <b>Die Zeile bleibt stehen</b> - was gelöscht ist, lässt sich
              nicht mehr nachsehen.
            </div>
            <Text name="grund" titel="Warum" pflicht wert={grund}
                  setze={setGrund} fehler={fehler} breite="100%" mehrzeilig
                  hinweis={'Ein gesperrter Schlüssel ohne Grund ist am '
                         + 'nächsten Tag nicht mehr zu deuten.'} />
          </>
        ) : (
          <div className="hinweis">
            Er wird wieder gültig. Gesperrt war er seit{' '}
            {k.gesperrt_am ? zeitpunkt(k.gesperrt_am) : 'unbekannt'}
            {k.gesperrt_grund && <>: „{k.gesperrt_grund}"</>}.
          </div>
        )}
      </Formular>
    </Dialog>
  );
}

function LoeschFenster({
  k, zu, fertig,
}: { k: Schluessel; zu: () => void; fertig: () => void }) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function los() {
    setLaeuft(true); setFehler(null);
    try { await api.apiSchluesselLoeschen(k.id, grund); fertig(); }
    catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
  }

  return (
    <Dialog titel="Schlüssel löschen" untertitel={`${k.name} (${k.kennung})`}
            breite={620} zu={zu}>
      <Formular geaendert={!!grund} laeuft={laeuft} fehler={fehler}
                speichern={los} knopfText="Endgültig löschen">
        <div className="hinweis">
          <b>Gelöscht ist weg.</b> Wer nur verhindern will, dass er weiter
          zieht, sperrt ihn - dann bleibt nachsehbar, was er wann getan hat.
          Bisher {k.zugriffe} Zugriffe.
        </div>
        <Text name="grund" titel="Warum" pflicht wert={grund} setze={setGrund}
              fehler={fehler} breite="100%" mehrzeilig
              hinweis="Er steht danach im Protokoll, mit Name und Kennung." />
      </Formular>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

/**
 * WAS GENAU DIESER SCHLÜSSEL DARF.
 *
 *   ORIGINALTEXT MARVIN (06.09.2026)
 *     „bzw wie kann er die verfügbaren APIs denn sehen?"
 *
 * Der Reiter „Wege" daneben beantwortet „was gibt es überhaupt". Wer einen
 * Schlüssel weitergibt, hat die engere Frage: **was kann DER.** Zwischen
 * „75 Rechte" in der Spalte und „diese 142 Wege" liegt eine Rechnung, und die
 * macht der SERVER - hier wird sie nur angezeigt. Eine zweite Fassung im
 * Browser liefe beim ersten neuen Riegel auseinander, und die Sperrliste, die
 * NUR für einen Schlüssel gilt, wäre die erste, die dabei vergessen wird.
 */
function DarfFenster({ k, zu }: { k: Schluessel; zu: () => void }) {
  const [daten, setDaten] = useState<
    { wege: Weg[]; rechte: string[]; insgesamt: number } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');

  useEffect(() => {
    api.apiSchluesselWege(k.id)
      .then((a) => setDaten({ wege: a.wege, rechte: a.rechte,
                              insgesamt: a.insgesamt }))
      .catch((f) => setFehler(ausFehler(f)));
  }, [k.id]);

  const s = suche.trim().toLowerCase();
  const wege = (daten?.wege ?? []).filter(
    (w) => !s || (w.weg + ' ' + w.methode).toLowerCase().includes(s));

  return (
    <Dialog titel="Was dieser Schlüssel darf"
            untertitel={`${k.name} (${k.kennung}), ${k.benutzer}`}
            breite={860} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {!daten
        ? <Leer text="Wird geholt." />
        : (
          <>
            <div className="hinweis">
              <b>{wege.length === daten.wege.length
                    ? `${daten.wege.length} von ${daten.insgesamt} Wegen.`
                    : `${wege.length} von ${daten.wege.length} gezeigt.`}</b>{' '}
              Gerechnet aus den {daten.rechte.length} Rechten dieses Schlüssels
              und der Sperrliste - {daten.insgesamt - daten.wege.length} Wege
              geht er nicht.
              {!k.aktiv && (
                <div style={{ marginTop: 6 }}>
                  {/* GESPERRT IST GESPERRT. Ohne diesen Satz liest sich die
                      Liste wie eine Erlaubnis, und der Schlüssel antwortet
                      trotzdem mit 401. */}
                  <b>Er ist gesperrt</b> und geht zurzeit gar keinen davon.
                </div>
              )}
            </div>
            {/* DIE ADRESSE ZUM ABSCHREIBEN. Wer das fremde System baut,
                braucht sie genau so, wie sie dasteht - mit `:kennung` darin.
                Die Anleitung liegt als `docs/api.md` bei. */}
            <div className="reihe" style={{ marginBottom: 10 }}>
              <input value={suche} onChange={(e) => setSuche(e.target.value)}
                     autoComplete="off" placeholder="Weg suchen"
                     style={{ minWidth: 240 }} />
              <button className="knopf"
                      onClick={() => navigator.clipboard?.writeText(
                        daten.wege.map((w) => `${w.methode} ${w.weg}`).join('\n'))}>
                Liste in die Zwischenablage
              </button>
            </div>
            {!wege.length
              ? <Leer text="Nichts gefunden." />
              : (
                <table className="liste">
                  <thead>
                    <tr><th style={{ width: 90 }}>Methode</th><th>Weg</th></tr>
                  </thead>
                  <tbody>
                    {wege.map((w) => (
                      <tr key={w.methode + w.weg}>
                        <td><code>{w.methode}</code></td>
                        <td><code><Bruchtext text={w.weg} /></code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            <p className="klein leise" style={{ marginTop: 10 }}>
              Dasselbe fragt das fremde System selbst ab:{' '}
              <code>GET /api/ich/wege</code> mit seinem Schlüssel.
            </p>
          </>
        )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

/**
 * DIE ANLEITUNG, IM BILDSCHIRM.
 *
 * ORIGINALTEXT MARVIN (06.09.2026)
 *   „dann ist noch der offene punkt mit der API schnittstelle mit token,
 *    einer kurzen anleitung zur einrichtung, damit man die daten dort
 *    hochladen und eintragen kann, aber auch auslesen."
 * ORIGINALTEXT MARVIN (07.09.2026)
 *   „auch den punkt mit den APIs steht noch aus."
 *
 * SIE LAG SEIT .122 ALS `docs/api.md` BEI - also als Datei auf dem Server.
 * Wer sie lesen will, meldet sich über SSH an; im Innendienst tut das
 * niemand. **Eine Anleitung, die man nicht findet, ist keine.** Sie steht
 * jetzt dort, wo die Schlüssel entstehen.
 *
 * DIE ADRESSE KOMMT VOM SERVER und nicht aus `location.origin`: bei einem
 * Proxy davor ist die zweite eine Angabe aus zweiter Hand. Der TEXT steht
 * hier - er ändert sich nicht mit dem Betrieb.
 *
 * UND ES STEHT KEIN SCHLÜSSEL DARIN. Das Beispiel trägt `mds_…`; wer einen
 * echten hineinschriebe, hätte ihn danach in jedem Bildschirmfoto.
 */
function Anleitung({ basis, kanaele }:
                   { basis: string | null; kanaele: Kanalstand | null }) {
  const b = basis || 'https://ftth.mds-innovation.com';
  const [kopiert, setKopiert] = useState<string | null>(null);

  /* KOPIEREN IST DER GANZE ZWECK EINES BEISPIELS. Wer eine Zeile mit
     `curl -H "Authorization: …"` abschreibt, tippt beim dritten Mal daneben -
     und sucht den Fehler danach im Schlüssel. */
  function kopiere(text: string, was: string) {
    navigator.clipboard?.writeText(text)
      .then(() => { setKopiert(was); setTimeout(() => setKopiert(null), 2000); })
      .catch(() => setKopiert(null));
  }

  const beispiele: { was: string; erklaerung: string; zeile: string }[] = [
    {
      was: 'Auslesen',
      erklaerung: 'Die Arbeitsliste, wie sie der Mensch sieht, an dem der '
                + 'Schlüssel hängt. Mit Schlüssel kommen 1000 je Seite, ohne '
                + 'dass man proSeite angeben muss. Steht in der Antwort '
                + '"vollstaendig": false, gibt es mehr: dann mit '
                + '"&seite=<naechste_seite>" weiterholen, bis '
                + '"vollstaendig": true kommt.',
      zeile: `curl -i -H "Authorization: Bearer mds_…" \\\n  "${b}/api/auftraege"`,
    },
    {
      /* DER WEG, DEN EIN ABHOLER AB EIN PAAR HUNDERT AUFTRÄGEN NEHMEN SOLL.
         Marvin, 21.09.2026: „dieser wert wird über die zeit auch nicht
         reichen. wir arbeiten ja dauerhaft immer weiter." */
      was: 'Nur Änderungen holen',
      erklaerung: 'Den Wert "stand" aus der vorigen Antwort mitgeben. Es '
                + 'kommen nur die Aufträge, an denen sich seither etwas '
                + 'geändert hat, dazu ein neuer "stand" für den Lauf danach. '
                + 'Das wächst nicht mit dem Bestand, sondern mit der Arbeit. '
                + 'Ein archivierter Auftrag kommt mit und trägt '
                + '"archiviert": true, damit die Gegenseite ihn herausnimmt.',
      zeile: `curl -H "Authorization: Bearer mds_…" \\\n`
           + `  "${b}/api/auftraege?geaendert_seit=2026-09-21T06:00:00Z"`,
    },
    {
      was: 'Eintragen',
      erklaerung: 'Ein Eintrag in die Auftragsinfo. JSON hinein, JSON heraus.',
      zeile: `curl -X POST -H "Authorization: Bearer mds_…" \\\n`
           + `  -H "Content-Type: application/json" \\\n`
           + `  -d '{"text":"Von aussen eingetragen"}' \\\n`
           + `  "${b}/api/auftraege/<Auftragskennung>/kommentar"`,
    },
    {
      was: 'Hochladen',
      erklaerung: 'Eine Datei geht als Mehrteil-Sendung hinein - genau wie '
                + 'beim Hochladen über den Bildschirm. Der Ordner steht VOR '
                + 'der Datei: einer, der dahinter steht, kommt zu spät. Was '
                + 'die Datei danach durchläuft und wo sie bleibt, steht '
                + 'unten unter „Die Kanäle".',
      zeile: `curl -X POST -H "Authorization: Bearer mds_…" \\\n`
           + `  -F "ordner=Montage/05 Sonstiges" \\\n`
           + `  -F "datei=@nachweis.pdf" \\\n`
           + `  "${b}/api/auftraege/<Auftragskennung>/dateien"`,
    },
  ];

  return (
    <>
      <Karte titel="In 5 Schritten angeschlossen">
        <ol className="anleitungsschritte">
          <li>
            <b>Zugang wählen.</b> Ein Schlüssel hängt immer an einem Menschen
            und <b>kann nie mehr als er</b>. Wer einem fremden System nur
            Leserechte geben will, legt dafür einen eigenen Zugang an.
          </li>
          <li>
            <b>Schlüssel anlegen</b> (Knopf oben rechts). Der Klartext steht{' '}
            <b>genau einmal</b> da - es gibt keinen Weg, der ihn ein zweites
            Mal herausgibt. Wer ihn verliert, sperrt ihn und legt einen neuen
            an.
          </li>
          <li>
            <b>Rechte abschneiden</b>, wenn nötig. Leer heißt: so viel wie
            der Mensch. Was angekreuzt ist, ist die <b>Obergrenze</b>.
          </li>
          <li>
            <b>Im fremden System eintragen</b>, als Kopfzeile{' '}
            <code>Authorization: Bearer mds_…</code>. Die Adresse dieses
            Systems ist <code>{b}</code>.
          </li>
          <li>
            <b>Ausprobieren</b> - eine der Zeilen unten. Kommt ein{' '}
            <code>401</code>, stimmt der Schlüssel nicht oder er ist gesperrt;
            ein <code>403</code> heißt, der Weg ist da und das Recht fehlt.
          </li>
        </ol>
        <div className="hinweis" style={{ marginTop: 12 }}>
          <b>Nur über eine verschlüsselte Verbindung.</b> Ein Schlüssel, der
          einmal über eine offene Leitung gegangen ist, ist ab diesem Moment
          nicht mehr geheim - der Server weist ihn deshalb ab.
        </div>
      </Karte>

      <Karte titel="Beispiele zum Abschreiben">
        {beispiele.map((x) => (
          <div key={x.was} style={{ marginBottom: 14 }}>
            <div className="reihe" style={{ alignItems: 'center' }}>
              <b className="wachsen">{x.was}</b>
              <button className="knopf klein"
                      onClick={() => kopiere(x.zeile, x.was)}>
                {kopiert === x.was ? 'kopiert' : 'kopieren'}
              </button>
            </div>
            <div className="klein leise" style={{ margin: '2px 0 6px' }}>
              {x.erklaerung}
            </div>
            <pre className="befehl"><code>{x.zeile}</code></pre>
          </div>
        ))}
        <p className="klein leise" style={{ marginBottom: 0 }}>
          <b>Welche Wege es gibt, steht im Reiter „Wege"</b> - mit dem Recht
          neben jedem. Diese Liste wird beim Start des Servers aus den
          wirklich angemeldeten Wegen aufgeschrieben und nicht von Hand
          gepflegt: eine gepflegte wäre beim ersten neuen Weg falsch, und zwar
          lautlos.
        </p>
      </Karte>

      <Kanaelekarte stand={kanaele} basis={b} />

      <Karte titel="Was die Antworten bedeuten">
        <table className="liste">
          <thead>
            <tr><th style={{ width: 90 }}>Kennzahl</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            {[['200', 'Alles in Ordnung.'],
              ['201', 'Angelegt.'],
              ['400', 'An der Sendung stimmt etwas nicht - der Text sagt was.'],
              ['401', 'Kein oder kein gültiger Schlüssel, oder er ist gesperrt.'],
              ['403', 'Den Weg gibt es, das Recht fehlt.'],
              ['404', 'Gibt es nicht - oder der Zugang dahinter darf ihn nicht sehen.'],
              ['409', 'Es hat sich seit dem Laden jemand anderes daran zu schaffen gemacht.'],
              ['413', 'Zu groß.'],
              ['415', 'Dieses Dateiformat kommt nicht herein.'],
              ['429', 'Zu schnell hintereinander.'],
             ].map(([n, t]) => (
              <tr key={n} style={{ cursor: 'default' }}>
                <td><code>{n}</code></td>
                <td className="klein">{t}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="klein leise" style={{ marginTop: 10, marginBottom: 0 }}>
          Die ausführliche Fassung für den Entwickler am anderen Ende liegt
          der Lieferung als <code>docs/api.md</code> bei.
        </p>
      </Karte>
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * DIE KANÄLE: WIE EINE DATEI IN DIESES HAUS KOMMT.
 *
 * ORIGINALTEXT MARVIN (08.09.2026, Punkt 4b der offenen Punkte)
 *   „API: Befehle für den Upload, und wie der Upload auf die einzelnen
 *    Kanäle läuft"
 *
 * DER BEFEHL IST DIE HALBE ANTWORT. Alle zehn Kanäle nehmen eine
 * Mehrteil-Sendung entgegen, und alle zehn sehen von aussen fast gleich aus;
 * **der Unterschied liegt darin, was mit der Datei DANACH geschieht.** Wer
 * eine Tabelle in den Auftragsordner legt, bekommt keine Fehlermeldung - er
 * bekommt eine Datei, die für immer dort liegt und aus der nie ein Auftrag
 * entsteht. Deshalb steht an jedem Kanal die STRECKE und das, was hinterher
 * BLEIBT.
 *
 * ES WIRD NICHTS ZUGEKLAPPT. Wer diesen Bildschirm aufmacht, richtet gerade
 * eine Schnittstelle ein und sucht seinen Kanal; ein Deckel auf einer Liste,
 * die vollständig sein muss, sagt, DASS etwas fehlt, und nie WAS.
 *
 * DREI AUSGÄNGE UND NICHT ZWEI. „Noch nicht geholt" ist still, „nicht
 * abrufbar" sagt es - eine Karte, die in beiden Fällen leer bleibt, ist von
 * einer erledigten Aufgabe nicht zu unterscheiden.
 */
function Kanaelekarte({ stand, basis }:
                      { stand: Kanalstand | null; basis: string }) {
  if (!stand) return null;
  const { kanaele, fuerSchluessel } = stand;
  if (!kanaele?.length) {
    return (
      <Karte titel="Die Kanäle: wie eine Datei ins Haus kommt">
        <Leer text="Die Kanäle sind gerade nicht abrufbar." />
      </Karte>
    );
  }

  return (
    <Karte titel="Die Kanäle: wie eine Datei ins Haus kommt">
      <p className="klein leise" style={{ marginTop: 0 }}>
        Der Befehl ist überall derselbe - eine Mehrteil-Sendung. Was ihn
        unterscheidet, steht unter <b>Strecke</b> und <b>Bleibt</b>:{' '}
        <b>wer den falschen Kanal wählt, bekommt keine Fehlermeldung, sondern
        ein anderes Ergebnis.</b> Die Marke daneben sagt, ob ein Schlüssel
        den Kanal überhaupt gehen darf - <b>gerechnet gegen dieselbe
        Sperrliste, die auch den Aufruf entscheidet</b>, und nicht
        hingeschrieben. Für eine Datei am Auftrag ist der umrandete der Weg;
        die übrigen offenen sind Werkzeuge der Umstellung, bei denen sonst ein
        Mensch eine Vorschau liest.
      </p>

      {kanaele.map((k) => (
        <div key={k.schluessel} className="hinweis"
             style={{ marginTop: 12,
                      borderColor: k.schluessel === fuerSchluessel
                        ? 'var(--akzent)' : undefined }}>
          <div className="reihe" style={{ alignItems: 'center' }}>
            <b className="wachsen">{k.name}</b>
            {/* EINE UMRANDUNG OHNE WORT IST KEINE AUSKUNFT. Der empfohlene
                Kanal trug nur eine andere Rahmenfarbe - wer sie sieht, weiss
                nicht, was sie ihm sagen will, und auf einem schmalen
                Bildschirm sieht er sie neben dem Nachbarn gar nicht. */}
            {k.schluessel === fuerSchluessel && (
              <span className="marke-zustand z-erledigt">
                für eine Datei am Auftrag
              </span>
            )}
            <span className={'marke-zustand '
                             + (k.mitSchluessel ? 'z-erledigt' : 'z-neu')}>
              {k.mitSchluessel ? 'mit Schlüssel' : 'nur von innen'}
            </span>
          </div>
          <div style={{ margin: '4px 0 6px' }}>
            <code><Bruchtext text={`${k.methode} ${basis}${k.weg}`} /></code>
          </div>
          {!k.gibtEs && (
            /* EIN HINWEIS, DER EINEN WEG NENNT, DEN ES NICHT GIBT, IST
               SCHLIMMER ALS KEIN HINWEIS. Der Server hält den Kanal gegen das
               Verzeichnis der wirklich angemeldeten Wege; fällt es
               auseinander, steht es hier und nicht erst bei dem, der die
               Zeile abschreibt. */
            <div className="klein" style={{ color: 'var(--ueberfaellig)' }}>
              <b>Diesen Weg meldet der Server nicht an.</b> Bitte im
              Prüfbericht melden - bis dahin trägt er nichts.
            </div>
          )}
          <div className="klein leise">
            {k.recht
              ? <>Recht: <code>{k.recht}</code></>
              /* KEIN RECHT HEISST NICHT ÜBERALL DASSELBE - beim Zusteller
                 eine eigene Tür mit eigenem Geheimnis, beim Profilbild „das
                 darf jeder für sich selbst". Der Satz zeigt deshalb auf die
                 Strecke, statt eine der beiden Bedeutungen zu behaupten. */
              : <>Kein Recht am Weg - warum, steht unter <b>Strecke</b>.</>}
            {!k.mehrteilig && ', Keine Mehrteil-Sendung, sondern JSON.'}
          </div>
          {k.felder.length > 0 && (
            <table className="liste" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 190 }}>Feld, in dieser Reihenfolge</th>
                  <th>Wozu</th>
                </tr>
              </thead>
              <tbody>
                {k.felder.map((f) => (
                  <tr key={f.name} style={{ cursor: 'default' }}>
                    <td>
                      <code><Bruchtext text={f.name} /></code>
                      <span className="klein leise" style={{ display: 'block' }}>
                        {f.art === 'datei' ? 'Datei' : 'Text'}
                        {f.pflicht ? ', Pflicht' : ', freiwillig'}
                      </span>
                    </td>
                    <td className="klein">{f.zweck}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="klein" style={{ marginTop: 8 }}>
            <b>Strecke:</b> {k.strecke}
          </div>
          <div className="klein" style={{ marginTop: 4 }}>
            <b>Bleibt:</b> {k.bleibt}
          </div>
        </div>
      ))}

      <p className="klein leise" style={{ marginTop: 12, marginBottom: 0 }}>
        <b>Die Reihenfolge der Teile zählt.</b> Der Server liest die Sendung
        von vorn nach hinten, während sie ankommt; ein Feld, das HINTER seiner
        Datei steht, kommt zu spät. Das ist die Bauart einer Mehrteil-Sendung
        und keine Schwäche.
      </p>
    </Karte>
  );
}

// ---------------------------------------------------------------------------

function Wegeliste({ wege }: { wege: Weg[] | null }) {
  const [suche, setSuche] = useState('');
  if (!wege) return <Karte><Leer text="Wird geladen." /></Karte>;

  const s = suche.trim().toLowerCase();
  const gezeigt = s
    ? wege.filter((w) => (w.weg + ' ' + w.methode + ' ' + w.rechte.join(' '))
                          .toLowerCase().includes(s))
    : wege;

  return (
    <Karte titel="Die Wege dieser Plattform">
      <p className="klein leise" style={{ marginTop: 0 }}>
        {/* GEMESSEN UND NICHT GEPFLEGT. Eine Liste von Hand wäre beim ersten
            neuen Weg falsch - und zwar lautlos. */}
        Beim Start des Servers aus den wirklich angemeldeten Wegen
        aufgeschrieben. „Kein Recht" heißt nicht „offen": diese Wege ziehen
        ihre Grenze in der Abfrage oder setzen einen Menschen voraus.
      </p>
      <div className="reihe" style={{ marginBottom: 10 }}>
        <input value={suche} onChange={(e) => setSuche(e.target.value)}
               autoComplete="off" placeholder="Weg oder Recht suchen"
               style={{ minWidth: 240 }} />
        {s && (
          <span className="klein leise">
            {gezeigt.length} von {wege.length}
          </span>
        )}
      </div>
      {!gezeigt.length
        ? <Leer text="Nichts gefunden." />
        : (
          <table className="liste">
              <thead>
                <tr><th>Methode</th><th>Weg</th><th>Recht</th></tr>
              </thead>
              <tbody>
                {gezeigt.map((w) => (
                  <tr key={w.methode + w.weg}>
                    <td><code>{w.methode}</code></td>
                    <td><code><Bruchtext text={w.weg} /></code></td>
                    <td>
                      {w.rechte.length
                        ? <>
                            {w.rechte.map((r) => <code key={r}>{r}</code>)
                              .reduce((a, b) => <>{a}{' oder '}{b}</>)}
                            {!w.eines && w.rechte.length > 1 && ' (alle)'}
                          </>
                        : <span className="klein leise">kein Recht an der Route</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
    </Karte>
  );
}


/**
 * WARUM SIEHT EIN PARTNER DIESEN AUFTRAG NICHT?
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (18.09.2026, mit 2 Bildschirmfotos)
 *   „wir haben probleme mit der api. die daten laufen nicht sauber über die
 *    schnitt stelle. so fehlen dem partner zb. die aufträge vom bild 1 in
 *    seinem system über die api. die sind ein beispiel von aktuell. bitte
 *    valide prüfen, das muss sauber funktionieren."
 *
 * **DIE ANTWORT „0 VON 0" GIBT KEINEN GRUND HER.** Ein Auftrag kann aus 4
 * Gründen fehlen, und keiner davon meldet sich: archiviert, Ort gesperrt,
 * kein Gewerk dieser Firma, oder Schlüssel/Zugang abgelaufen. Für ein fremdes
 * System sieht jeder dieser Fälle gleich aus wie „den Auftrag gibt es nicht".
 *
 * HIER WIRD JEDER EINZELN GEMESSEN, mit derselben Bedingung, die ihn auch in
 * der Liste zieht. Geraten wird nichts.
 */
function Sichtbarkeitspruefung() {
  const [suche, setSuche] = useState('');
  const [firma, setFirma] = useState('');
  const [firmen, setFirmen] = useState<any[]>([]);
  const [d, setD] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.subunternehmer().then((f: any) => setFirmen(f ?? [])).catch(() => {});
  }, []);

  async function pruefe() {
    setLaeuft(true); setFehler(null);
    try { setD(await api.apiSichtbarkeit(suche.trim(), firma || undefined)); }
    catch (f) { setFehler(ausFehler(f)); setD(null); }
    finally { setLaeuft(false); }
  }

  return (
    <Karte titel="Warum sieht ein Partner diesen Auftrag nicht?">
      <div className="leib">
        <p className="klein leise" style={{ marginTop: 0 }}>
          Ein Schlüssel geht denselben Weg wie ein Bildschirm - er sieht, was
          der Zugang sehen darf, an dem er hängt. Fehlt einem Partner ein
          Auftrag, liegt es an einer von 4 Bedingungen, und die Antwort der
          Schnittstelle nennt keine davon: sie lautet dann schlicht
          „0 von 0". Hier steht der Grund.
        </p>

        <Fehlerkasten lage={fehler} />

        <div className="feldreihe" style={{ marginBottom: 12 }}>
          <label className="feldpaar" style={{ flex: 1, minWidth: 260 }}>
            <span>Auftrag</span>
            <input value={suche} placeholder="KLS, SM, BAFA oder Anschrift"
                   onChange={(e) => setSuche(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') void pruefe(); }} />
          </label>
          <label className="feldpaar" style={{ minWidth: 240 }}>
            <span>Firma</span>
            <select value={firma} onChange={(e) => setFirma(e.target.value)}>
              <option value="">ohne Firma (nur den Auftrag ansehen)</option>
              {firmen.map((f: any) => (
                <option key={f.id} value={f.id}>{f.firma}</option>
              ))}
            </select>
          </label>
          <button className="knopf haupt" disabled={laeuft || suche.trim().length < 3}
                  onClick={() => void pruefe()}>
            {laeuft ? 'Einen Moment' : 'Prüfen'}
          </button>
        </div>

        {/* DER ZUSTAND DES SCHLÜSSELS IST EINE ZWEITE TÜR.
            Die Schlüsselliste zeigt nur `k.aktiv`. Ein Schlüssel an einem
            Zugang, den jemand später stillgelegt hat, steht dort weiter als
            „aktiv" und antwortet trotzdem mit 401. */}
        {d?.schluessel?.length > 0 && (
          <>
            <h4>Die Schlüssel dieser Firma</h4>
            <table className="liste">
              <thead>
                <tr><th>Schlüssel</th><th>Zugang</th><th>Stand</th></tr>
              </thead>
              <tbody>
                {d.schluessel.map((k: any) => (
                  <tr key={k.kennung} style={{ cursor: 'default' }}>
                    <td className="klein">{k.name}<div className="leise">{k.kennung}</div></td>
                    <td className="klein">{k.zugang}</td>
                    <td className="klein">
                      {!k.aktiv ? (
                        <span className="marke-zustand z-ueberfaellig">gesperrt</span>
                      ) : k.abgelaufen ? (
                        <span className="marke-zustand z-ueberfaellig">
                          Frist abgelaufen
                        </span>
                      ) : !k.zugang_aktiv ? (
                        <span className="marke-zustand z-ueberfaellig">
                          Zugang stillgelegt
                        </span>
                      ) : k.zugang_wartet ? (
                        <span className="marke-zustand z-ueberfaellig">
                          Zugang wartet auf Freigabe
                        </span>
                      ) : (
                        <span className="marke-zustand z-erledigt">geht</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {d && d.auftraege.length === 0 && (
          <Leer text="Unter diesem Suchwort steht kein Auftrag - auch nicht im Archiv." />
        )}

        {d?.auftraege?.map((a: any) => (
          <div key={a.id} className="karte" style={{ marginTop: 10 }}>
            <h3>
              {a.anschrift}
              <span className="weg">
                KLS {a.kls_id ?? '-'}, SM {a.sm_nummer ?? '-'}
              </span>
              {a.sichtbar
                ? <span className="marke-zustand z-erledigt">sichtbar</span>
                : <span className="marke-zustand z-ueberfaellig">nicht sichtbar</span>}
            </h3>
            <div className="leib">
              {a.gruende.length === 0 ? (
                <p className="klein" style={{ marginTop: 0 }}>
                  Dieser Auftrag geht über die Schnittstelle hinaus. Seit
                  .178 bekommt ein Abruf mit API-Schlüssel ohne eigene
                  Angabe von <code>proSeite</code> die vollen 1000 je Seite,
                  und die Sortierung ist eindeutig, so dass das Blättern
                  keine Zeile mehr verliert. Kommt der Auftrag trotzdem
                  nicht an, sind es mehr als 1000: dann sagt die Antwort
                  <code> vollstaendig: false</code> und nennt in
                  <code> naechste_seite</code> und im <code>Link</code>-Kopf,
                  wo es weitergeht. Besser als blättern ist ab dieser Grösse
                  ohnehin <code>geaendert_seit</code>.
                </p>
              ) : (
                <ul className="klein" style={{ marginTop: 0 }}>
                  {a.gruende.map((g: string, i: number) => <li key={i}>{g}</li>)}
                </ul>
              )}
              <div className="klein leise">
                Gewerke:{' '}
                {a.gewerke.length === 0
                  ? 'keine'
                  : a.gewerke.map((g: any, i: number) => (
                      <span key={i}>
                        {i > 0 && ', '}
                        {g.art}
                        {g.firma ? ` (${g.firma})` : ' (ohne Firma)'}
                        {g.storniert ? ' storniert' : ''}
                      </span>
                    ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Karte>
  );
}
